"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Keyboard, Thumbs } from "swiper/modules"
import { ChevronLeft, ChevronRight, X, Maximize2, Share2 } from "lucide-react"
import Image from "next/image"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { Swiper as SwiperType } from "swiper"

import "swiper/css"
import "swiper/css/navigation"
import { cn } from "@/core/lib/utils"

type ImageType = {
  url: string
  alt?: string
  description?: string
}

type ProductGalleryProps = {
  images: ImageType[]
  product: {
    title?: string
    discount_percent: number
    discount_title: string
    thumbnail?: ImageType
  }
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)")
    // eslint-disable-next-line
    setIsDesktop(mql.matches)
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mql.addEventListener("change", handler)
    return () => mql.removeEventListener("change", handler)
  }, [])

  return isDesktop
}

export default function ProductGallery({ images, product }: ProductGalleryProps) {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
  const [modalSwiper, setModalSwiper] = useState<SwiperType | null>(null)
  const [mainHeight, setMainHeight] = useState(0)
  const mainRef = useRef<HTMLDivElement>(null)
  const isDesktop = useIsDesktop()

  const displayImages: ImageType[] = product?.thumbnail?.url
    ? [
      {
        url: product.thumbnail.url,
        alt: product.title || "Product main image",
      },
      ...images,
    ]
    : images

  useEffect(() => {
    if (isGalleryOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isGalleryOpen])

  // Measure main slider height so desktop thumbs match it
  useEffect(() => {
    const el = mainRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      setMainHeight(entry.contentRect.height)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Reset thumbsSwiper when direction changes (desktop <-> mobile)
  useEffect(() => {
    // eslint-disable-next-line
    setThumbsSwiper(null)
  }, [isDesktop])

  const handleThumbsSwiper = useCallback((swiper: SwiperType) => {
    setThumbsSwiper(swiper)
  }, [])

  // When main slider changes, update index and auto-scroll thumbs
  const handleMainSlideChange = useCallback(
    (swiper: SwiperType) => {
      const realIndex = swiper.realIndex
      setCurrentIndex(realIndex)
      if (thumbsSwiper && !thumbsSwiper.destroyed) {
        thumbsSwiper.slideTo(realIndex)
      }
    },
    [thumbsSwiper]
  )

  if (displayImages.length === 0) return null

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.title || "Product",
          text: "Check out this product",
          url: window.location.href,
        })
      } catch (err) {
        console.log("Error sharing:", err)
      }
    }
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const thumbSlides = displayImages.map((img, index) => (
    <SwiperSlide key={index}>
      <div
        className={cn(
          "relative aspect-square overflow-hidden cursor-pointer border-2 rounded-md transition-all duration-300",
          "border-transparent opacity-60 hover:opacity-100",
          currentIndex === index && "border-primary opacity-100"
        )}
      >
        <Image
          src={img.url || "/placeholder.svg"}
          alt={img.alt || "Thumbnail"}
          fill
          className="object-cover"
          sizes="96px"
        />
      </div>
    </SwiperSlide>
  ))

  return (
    <div className="w-full">
      <div className="relative flex gap-4 flex-col md:flex-row-reverse">
        {/* Main Slider */}
        <div ref={mainRef} className="relative overflow-hidden w-full md:flex-1">
          <Swiper
            modules={[Navigation, Keyboard, Thumbs]}
            thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
            navigation={{
              prevEl: ".main-prev",
              nextEl: ".main-next",
            }}
            keyboard={{ enabled: true }}
            slidesPerView={1}
            spaceBetween={0}
            loop={displayImages.length > 1}
            speed={400}
            onSlideChange={handleMainSlideChange}
            className="w-full aspect-square"
          >
            {displayImages.map((img, index) => (
              <SwiperSlide key={index}>
                <div
                  className="relative w-full h-full cursor-pointer group"
                  onClick={() => {
                    setCurrentIndex(index)
                    setIsGalleryOpen(true)
                  }}
                >
                  <Image
                    src={img.url || "/placeholder.svg"}
                    alt={img.description || img.alt || product.title || "Product image"}
                    fill
                    className="object-cover transition-transform duration-300"
                    sizes="(max-width: 768px) 100vw, 600px"
                    priority={index === 0}
                  />
                  {product.discount_percent > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute top-4 right-4 text-sm font-bold shadow-lg z-10"
                    >
                      {product.discount_percent}% {product.discount_title}
                    </Badge>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Navigation Buttons */}
          {displayImages.length > 1 && (
            <>
              <Button
                size="icon"
                variant="secondary"
                className="main-prev absolute right-2 top-1/2 -translate-y-1/2 z-20 shadow-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="main-next absolute left-2 top-1/2 -translate-y-1/2 z-20 shadow-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </>
          )}

          {/* Image Counter */}
          {displayImages.length > 1 && (
            <div className="absolute bottom-4 left-4 bg-foreground/70 rounded-sm text-background px-3 py-1.5 text-sm font-medium z-10">
              {currentIndex + 1} / {displayImages.length}
            </div>
          )}
        </div>

        {/* Thumbnail Slider - No separate nav, auto-syncs with main slider */}
        {displayImages.length > 1 && (
          <>
            {/* Mobile: Horizontal thumbnails */}
            {!isDesktop && (
              <div className="relative w-full">
                <Swiper
                  key="horizontal"
                  modules={[Thumbs]}
                  watchSlidesProgress
                  onSwiper={handleThumbsSwiper}
                  direction="horizontal"
                  slidesPerView={4}
                  spaceBetween={8}
                >
                  {thumbSlides}
                </Swiper>
              </div>
            )}

            {/* Desktop: Vertical thumbnails matching main slider height */}
            {isDesktop && mainHeight > 0 && (
              <div
                className="relative w-20 overflow-hidden"
                style={{ height: mainHeight }}
              >
                <Swiper
                  key="vertical"
                  modules={[Thumbs]}
                  watchSlidesProgress
                  onSwiper={handleThumbsSwiper}
                  direction="vertical"
                  slidesPerView={Math.floor((mainHeight + 8) / (80 + 8))}
                  spaceBetween={8}
                  style={{ height: "100%" }}
                >
                  {thumbSlides}
                </Swiper>
              </div>
            )}
          </>
        )}
      </div>

      {/* Enhanced Modal */}
      {isGalleryOpen && (
        <div className="fixed inset-0 z-50 bg-background/95 animate-in fade-in duration-300">
          {/* Header Controls */}
          <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4">
            <div className="flex items-center gap-2">
              <span className="text-foreground font-medium text-lg">
                {currentIndex + 1} / {displayImages.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                onClick={toggleFullscreen}
                className="text-foreground hover:bg-muted h-10 w-10"
              >
                <Maximize2 className="w-5 h-5" />
              </Button>

              <Button
                size="icon"
                variant="ghost"
                onClick={handleShare}
                className="text-foreground hover:bg-muted h-10 w-10"
              >
                <Share2 className="w-5 h-5" />
              </Button>

              <Button
                size="icon"
                variant="ghost"
                onClick={() => setIsGalleryOpen(false)}
                className="text-foreground hover:bg-muted h-10 w-10"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
          </div>

          {/* Navigation Arrows */}
          {displayImages.length > 1 && (
            <>
              <Button
                size="icon"
                variant="secondary"
                className="modal-prev absolute right-4 top-1/2 -translate-y-1/2 z-50 h-12 w-12 shadow-2xl"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="modal-next absolute left-4 top-1/2 -translate-y-1/2 z-50 h-12 w-12 shadow-2xl"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
            </>
          )}

          {/* Modal Swiper */}
          <Swiper
            modules={[Navigation, Keyboard]}
            navigation={{
              prevEl: ".modal-prev",
              nextEl: ".modal-next",
            }}
            keyboard={{ enabled: true }}
            initialSlide={currentIndex}
            loop={displayImages.length > 1}
            speed={400}
            onSwiper={setModalSwiper}
            onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
            className="w-full h-full"
          >
            {displayImages.map((img, i) => (
              <SwiperSlide key={i} className="!flex items-center !justify-center">
                <Image
                  src={img.url || "/placeholder.svg"}
                  alt={img.description || img.alt || "Product image"}
                  width={1920}
                  height={1080}
                  className="max-w-full h-svh max-h-[calc(100vh-8rem)] w-auto object-contain"
                  priority={i === currentIndex}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Thumbnail Strip in Modal */}
          {displayImages.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-background/80 to-transparent">
              <div className="flex gap-2 justify-center max-w-4xl mx-auto overflow-x-auto py-2">
                {displayImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => modalSwiper?.slideTo(index)}
                    className={cn(
                      "relative shrink-0 w-16 h-16 overflow-hidden border-2 rounded-md transition-all duration-300",
                      currentIndex === index
                        ? "border-primary scale-110 shadow-lg"
                        : "border-transparent opacity-60 hover:opacity-100"
                    )}
                  >
                    <Image
                      src={img.url || "/placeholder.svg"}
                      alt={`Thumbnail ${index + 1}`}
                      fill
                      className="object-cover"
                      sizes="64px"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
